import React, { useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { useAuth } from '../AuthContext';
import { CUSTOMER_BY_ID_QUERY, CREATE_ADDRESS_MUTATION, UPDATE_CUSTOMER_ARCHIVE_MUTATION } from '../graphql/customers';
import { CREATE_JOB_MUTATION } from '../graphql/jobs';
import AddressPicker, { AddressPickerHandle } from '../components/Address/AddressPicker';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import './CustomerDetail.css';

interface Address {
  id: number;
  street: string;
  city: string;
  state: string;
  zip: string;
  addressType: string;
}

interface Job {
  id: number;
  name: string;
  status: string;
  createdAt: string;
  archivedAt: string | null;
}

interface CustomerData {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  archivedAt: string | null;
  addressesByCustomerId: { nodes: Address[] };
  jobsByCustomerId: { nodes: Job[] };
}

export default function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [showJobForm, setShowJobForm] = useState(false);
  const [jobFormData, setJobFormData] = useState({ name: '', description: '', addressId: '' as number | '' });
  const [jobFormError, setJobFormError] = useState<string | null>(null);
  const [jobSaving, setJobSaving] = useState(false);
  const jobAddressPickerRef = useRef<AddressPickerHandle>(null);

  const { data, loading, error, refetch } = useQuery(CUSTOMER_BY_ID_QUERY, {
    variables: { id: parseInt(id!) },
    skip: !id,
  });

  const [createAddress] = useMutation(CREATE_ADDRESS_MUTATION);
  const [updateArchive] = useMutation(UPDATE_CUSTOMER_ARCHIVE_MUTATION);
  const [createJob] = useMutation(CREATE_JOB_MUTATION);

  const handleArchive = async () => {
    if (!customer) return;
    const isArchived = customer.archivedAt !== null && customer.archivedAt !== undefined;

    if (!window.confirm(`Are you sure you want to ${isArchived ? 'unarchive' : 'archive'} this customer?`)) {
      return;
    }

    try {
      await updateArchive({
        variables: {
          id: customer.id,
          archivedAt: isArchived ? null : new Date().toISOString(),
        },
      });
      navigate('/dashboard');
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  const customer: CustomerData | undefined = data?.customerById;

  const [formData, setFormData] = useState({
    street: '',
    city: '',
    state: '',
    zip: '',
    addressType: 'primary' as const,
  });

  const handleAddAddress = async () => {
    if (!formData.street || !formData.city || !formData.state || !formData.zip) {
      alert('Please fill in all address fields');
      return;
    }

    try {
      await createAddress({
        variables: {
          customerId: parseInt(id!),
          domainId: user?.domain_id,
          street: formData.street,
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
          addressType: formData.addressType,
        },
      });
      // Reset form
      setFormData({ street: '', city: '', state: '', zip: '', addressType: 'primary' });
      setShowAddressForm(false);
      // Refetch customer data
      // Using Apollo cache to update would be better, but for now we'll let the user refresh
      window.location.reload();
    } catch (err: any) {
      alert(`Error adding address: ${err.message}`);
    }
  };

  if (loading) {
    return <div className="customer-detail-page">Loading...</div>;
  }

  if (error || !customer) {
    return (
      <div className="customer-detail-page">
        <button onClick={() => navigate('/customers')} className="back-button btn-icon">
          <ArrowBackIcon fontSize="small" /> Back to Dashboard
        </button>
        <div className="error-message">Customer not found</div>
      </div>
    );
  }

  return (
    <div className="customer-detail-page">
      <button onClick={() => navigate('/customers')} className="back-button btn-icon">
        <ArrowBackIcon fontSize="small" /> Back to Dashboard
      </button>

      <div className="detail-container">
        <div className="detail-header">
          <div>
            <h1>{customer.name}</h1>
            {customer.archivedAt && <div className="archived-badge">Archived</div>}
          </div>
          <div className="header-actions">
            <button className="archive-button btn-icon" onClick={handleArchive}>
              {customer.archivedAt ? <UnarchiveIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}
              {customer.archivedAt ? 'Unarchive' : 'Archive'}
            </button>
            <button className="edit-button btn-icon" onClick={() => setEditing(!editing)}>
              {editing ? <CloseIcon fontSize="small" /> : <EditIcon fontSize="small" />}
              {editing ? 'Cancel' : 'Edit'}
            </button>
          </div>
        </div>

        {/* Customer Info */}
        <div className="section">
          <h2>Customer Information</h2>
          <div className="info-grid">
            <div className="info-row">
              <label>Name</label>
              <div>{customer.name}</div>
            </div>
            <div className="info-row">
              <label>Email</label>
              <div>
                {customer.email ? (
                  <a href={`mailto:${customer.email}`}>{customer.email}</a>
                ) : (
                  <span className="empty">—</span>
                )}
              </div>
            </div>
            <div className="info-row">
              <label>Phone</label>
              <div>
                {customer.phone ? (
                  <a href={`tel:${customer.phone}`}>{customer.phone}</a>
                ) : (
                  <span className="empty">—</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Addresses */}
        <div className="section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2>Addresses ({customer.addressesByCustomerId.nodes.length})</h2>
            <button className="add-button btn-icon" onClick={() => setShowAddressForm(!showAddressForm)}>
              {showAddressForm ? <CloseIcon fontSize="small" /> : <AddIcon fontSize="small" />}
              {showAddressForm ? 'Close' : 'Add Address'}
            </button>
          </div>

          {/* Add Address Form */}
          {showAddressForm && (
            <div className="address-form">
              <div className="form-group">
                <label>Street</label>
                <input
                  type="text"
                  value={formData.street}
                  onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                  placeholder="123 Main St"
                  className="input"
                />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Minneapolis"
                    className="input"
                  />
                </div>
                <div className="form-group" style={{ width: 100 }}>
                  <label>State</label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value.toUpperCase() })}
                    placeholder="MN"
                    maxLength={2}
                    className="input"
                  />
                </div>
                <div className="form-group" style={{ width: 120 }}>
                  <label>ZIP</label>
                  <input
                    type="text"
                    value={formData.zip}
                    onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                    placeholder="55401"
                    className="input"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Address Type</label>
                <select
                  value={formData.addressType}
                  onChange={(e) => setFormData({ ...formData, addressType: e.target.value as any })}
                  className="input"
                >
                  <option value="primary">Primary</option>
                  <option value="billing">Billing</option>
                  <option value="shipping">Shipping</option>
                </select>
              </div>
              <button onClick={handleAddAddress} className="save-button">
                Save Address
              </button>
            </div>
          )}

          {/* Address List */}
          {customer.addressesByCustomerId.nodes.length === 0 ? (
            <div className="empty-state">No addresses yet</div>
          ) : (
            <div className="addresses-list">
              {customer.addressesByCustomerId.nodes.map((addr) => (
                <div key={addr.id} className="address-card">
                  <div>
                    <div className="address-street">{addr.street}</div>
                    <div className="address-city-state">
                      {addr.city}, {addr.state} {addr.zip}
                    </div>
                  </div>
                  <span className="address-type-badge">{addr.addressType}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Jobs */}
        <div className="section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ margin: 0 }}>Jobs ({(customer.jobsByCustomerId?.nodes || []).filter(j => !j.archivedAt).length})</h2>
            {user?.role !== 'worker' && (
              <button
                className="add-button"
                onClick={() => setShowJobForm(true)}
              >
                + New Job
              </button>
            )}
          </div>

          {showJobForm && (
            <div style={{ padding: 16, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, marginBottom: 16 }}>
              {jobFormError && <div className="error-message">{jobFormError}</div>}
              <div className="form-group">
                <label>Job Name *</label>
                <input
                  className="input"
                  type="text"
                  placeholder="e.g., Spring 2026 Install"
                  value={jobFormData.name}
                  onChange={(e) => setJobFormData({ ...jobFormData, name: e.target.value })}
                  disabled={jobSaving}
                />
              </div>
              <div className="form-group">
                <label>Description (Optional)</label>
                <input
                  className="input"
                  type="text"
                  value={jobFormData.description}
                  onChange={(e) => setJobFormData({ ...jobFormData, description: e.target.value })}
                  disabled={jobSaving}
                />
              </div>
              <div className="form-group">
                <AddressPicker
                  ref={jobAddressPickerRef}
                  label="Job Address (Optional)"
                  addresses={customer.addressesByCustomerId.nodes}
                  value={jobFormData.addressId}
                  onChange={(addressId) => setJobFormData({ ...jobFormData, addressId })}
                  disabled={jobSaving}
                  onCreateAddress={async (fields) => {
                    const result = await createAddress({
                      variables: {
                        customerId: customer.id,
                        domainId: user?.domain_id,
                        street: fields.street,
                        city: fields.city,
                        state: fields.state,
                        zip: fields.zip,
                        addressType: 'primary',
                      },
                      refetchQueries: ['CustomerById'],
                    });
                    return result.data.createAddress.address;
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="save-button"
                  disabled={jobSaving}
                  onClick={async () => {
                    if (!jobFormData.name.trim()) { setJobFormError('Job name is required'); return; }
                    setJobSaving(true);
                    setJobFormError(null);
                    try {
                      const resolvedAddressId = await jobAddressPickerRef.current?.commit() ?? jobFormData.addressId;
                      await createJob({
                        variables: {
                          domainId: user!.domain_id,
                          customerId: customer.id,
                          name: jobFormData.name.trim(),
                          description: jobFormData.description.trim() || null,
                          addressId: resolvedAddressId || null,
                        },
                      });
                      setShowJobForm(false);
                      setJobFormData({ name: '', description: '', addressId: '' });
                      refetch();
                    } catch (err: any) {
                      setJobFormError(err.message || 'Failed to create job');
                    } finally {
                      setJobSaving(false);
                    }
                  }}
                >
                  {jobSaving ? 'Saving...' : 'Create Job'}
                </button>
                <button
                  style={{ padding: '10px 16px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer' }}
                  onClick={() => { setShowJobForm(false); setJobFormError(null); setJobFormData({ name: '', description: '', addressId: '' }); }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {(customer.jobsByCustomerId?.nodes || []).filter(j => !j.archivedAt).length === 0 ? (
            <div className="empty-state">No jobs yet</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '8px 0', textAlign: 'left', color: '#374151', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Job Name</th>
                  <th style={{ padding: '8px 0', textAlign: 'left', color: '#374151', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Created</th>
                </tr>
              </thead>
              <tbody>
                {(customer.jobsByCustomerId?.nodes || []).filter(j => !j.archivedAt).map((job) => (
                  <tr key={job.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 0' }}>
                      <a href={`/jobs/${job.id}`} style={{ color: '#3b82f6', fontWeight: 600, textDecoration: 'none' }}>
                        {job.name}
                      </a>
                    </td>
                    <td style={{ padding: '10px 0', color: '#6b7280' }}>{new Date(job.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
